import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  FileStack,
  History,
  Clock,
  AlertTriangle,
  Check,
  X,
  Pencil,
  Trash2,
  RotateCcw,
  Loader2,
} from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { PageHeader, FluentCard } from '@/components/layout/page-header'
import { useToast } from '@/components/ui/use-toast'
import { PermissionGate } from '@/components/auth/permission-gate'
import { useConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  useDocument,
  useUpdateDocumentField,
  useConfirmDeleteField,
  useRestoreField,
} from '@/lib/hooks/use-documents'
import {
  DocumentFieldConflictError,
  type DocumentFieldDto,
} from '@/lib/api/services/documents'

function statusBadgeClass(status: DocumentFieldDto['status']): string {
  switch (status) {
    case 'Active':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    case 'PendingDelete':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
    case 'Deleted':
      return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
    default:
      return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
  }
}

export default function DocumentDetailPage() {
  const params = useParams<{ id: string }>()
  const documentId = params.id ?? ''
  const { toast } = useToast()
  const { confirm, ConfirmDialogComponent } = useConfirmDialog()

  const [editingFieldId, setEditingFieldId] = useState<string | null>(null)
  const [draftValue, setDraftValue] = useState('')

  const { data: documentData, isLoading, isError, refetch } = useDocument(documentId)

  const updateFieldMutation = useUpdateDocumentField()
  const confirmDeleteMutation = useConfirmDeleteField()
  const restoreFieldMutation = useRestoreField()

  const startEditing = (field: DocumentFieldDto) => {
    setEditingFieldId(field.id)
    setDraftValue(field.value ?? '')
  }

  const cancelEditing = () => {
    setEditingFieldId(null)
    setDraftValue('')
  }

  const saveField = (field: DocumentFieldDto) => {
    updateFieldMutation.mutate(
      {
        documentId,
        fieldId: field.id,
        data: { value: draftValue, expectedVersion: field.version },
      },
      {
        onSuccess: () => {
          toast({ title: 'Saved', description: `"${field.displayName}" was updated.` })
          setEditingFieldId(null)
          setDraftValue('')
        },
        onError: (error) => {
          if (error instanceof DocumentFieldConflictError) {
            toast({
              title: 'Conflict detected',
              description:
                'This field changed on the server since you started editing. The latest value has been loaded.',
              variant: 'destructive',
            })
            refetch()
            setEditingFieldId(null)
            setDraftValue('')
          } else {
            toast({
              title: 'Error',
              description: error.message || 'Failed to update field.',
              variant: 'destructive',
            })
          }
        },
      }
    )
  }

  const handleConfirmDelete = async (field: DocumentFieldDto) => {
    const confirmed = await confirm({
      title: 'Confirm field deletion',
      description: `This will permanently mark "${field.displayName}" as deleted. This action cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'destructive',
    })
    if (!confirmed) return

    confirmDeleteMutation.mutate(
      { documentId, fieldId: field.id },
      {
        onSuccess: () => {
          toast({ title: 'Field deleted', description: `"${field.displayName}" was deleted.` })
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error.message || 'Failed to delete field.',
            variant: 'destructive',
          })
        },
      }
    )
  }

  const handleRestore = (field: DocumentFieldDto) => {
    restoreFieldMutation.mutate(
      { documentId, fieldId: field.id },
      {
        onSuccess: () => {
          toast({ title: 'Field restored', description: `"${field.displayName}" is active again.` })
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error.message || 'Failed to restore field.',
            variant: 'destructive',
          })
        },
      }
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
            <div className="h-4 w-96 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          </div>
        </div>
        <FluentCard>
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
        </FluentCard>
      </div>
    )
  }

  if (isError || !documentData) {
    return (
      <div className="space-y-6">
        <Link
          to="/documents"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Documents
        </Link>
        <FluentCard>
          <div className="flex flex-col items-center justify-center text-center py-12">
            <AlertTriangle className="h-8 w-8 text-red-500 mb-3" />
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              This document could not be loaded.
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </FluentCard>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Link
        to="/documents"
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Documents
      </Link>

      <PageHeader
        title={documentData.name}
        description={`Created from template "${documentData.templateName}" (v${documentData.templateVersionNumber})`}
        actions={
          <div className="flex items-center gap-2">
            <Badge
              className={
                documentData.status === 'Active'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
              }
            >
              {documentData.status}
            </Badge>
          </div>
        }
      />

      {/* Header info */}
      <div className="grid gap-4 sm:grid-cols-3">
        <FluentCard className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
            <FileStack className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{documentData.fieldCount}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Fields</p>
          </div>
        </FluentCard>
        <FluentCard className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-violet-400 to-purple-600">
            <History className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">v{documentData.documentVersion}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Document version</p>
          </div>
        </FluentCard>
        <FluentCard className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600">
            <Clock className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900 dark:text-white">
              {documentData.lastSyncedAt ? new Date(documentData.lastSyncedAt).toLocaleString() : 'Never'}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Last synced</p>
          </div>
        </FluentCard>
      </div>

      {/* Fields table */}
      <FluentCard className="overflow-hidden">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Fields</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Values bound into this document's content controls
          </p>
        </div>

        {documentData.fields.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-12">
            <FileStack className="h-8 w-8 text-gray-400 mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No fields on this document yet.</p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Field</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documentData.fields.map((field) => {
                  const isEditing = editingFieldId === field.id
                  const isSaving =
                    updateFieldMutation.isPending && updateFieldMutation.variables?.fieldId === field.id

                  return (
                    <TableRow key={field.id}>
                      <TableCell>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {field.displayName}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{field.name}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{field.sourceType}</Badge>
                      </TableCell>
                      <TableCell className="min-w-[240px]">
                        {isEditing ? (
                          <div className="flex flex-col gap-2">
                            <Textarea
                              value={draftValue}
                              onChange={(e) => setDraftValue(e.target.value)}
                              rows={2}
                              className="text-sm"
                              disabled={isSaving}
                              autoFocus
                            />
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                onClick={() => saveField(field)}
                                disabled={isSaving}
                                className="gap-1.5"
                              >
                                {isSaving ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Check className="h-3.5 w-3.5" />
                                )}
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={cancelEditing}
                                disabled={isSaving}
                                className="gap-1.5"
                              >
                                <X className="h-3.5 w-3.5" />
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                            {field.value || <span className="text-gray-400 italic">Empty</span>}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-500 dark:text-gray-400">v{field.version}</span>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(field.status)}`}
                        >
                          {field.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          {field.status === 'PendingDelete' ? (
                            <PermissionGate permissions={['templates.update']}>
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1.5"
                                onClick={() => handleRestore(field)}
                                disabled={restoreFieldMutation.isPending}
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                                Restore
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1.5 text-red-600 dark:text-red-400"
                                onClick={() => handleConfirmDelete(field)}
                                disabled={confirmDeleteMutation.isPending}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Confirm delete
                              </Button>
                            </PermissionGate>
                          ) : field.status === 'Active' && !isEditing ? (
                            <PermissionGate permissions={['templates.update']}>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="gap-1.5"
                                onClick={() => startEditing(field)}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                                Edit
                              </Button>
                            </PermissionGate>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </FluentCard>

      {ConfirmDialogComponent}
    </div>
  )
}
